import { useMemo, useState } from 'react'
import {
  groupByCategory,
  isSecondaryCategory,
  type CategoryGroup,
  type RawEdition,
} from './awards'
import { NomineeCard } from './NomineeCard'
import { useInLibrary } from '../library/useInLibrary'

interface Props {
  edition: RawEdition
  onOpenDetail: (tmdbId: number, type: 'movie' | 'tv') => void
}

/**
 * Renders every category for a ceremony — winners first, then the rest
 * of the nominees — and hides technical / craft awards behind a
 * "Show technical awards" toggle the way the desktop does.
 *
 * Responsive nominee grid: phone 1 / tablet 2 / laptop 3 / desktop 4 /
 * big desktop + TV 5. Winner gets a gold ring and, when there are at
 * least two rows of nominees, spans two columns so the card reads as
 * the hero of the category — a simplified take on the desktop grid
 * formula that keeps orphan rows tidy.
 */
export function CategoryList({ edition, onOpenDetail }: Props) {
  const allGroups = useMemo(() => groupByCategory(edition), [edition])
  const main = useMemo(
    () => allGroups.filter((g) => !isSecondaryCategory(g.categoryName)),
    [allGroups]
  )
  const secondary = useMemo(
    () => allGroups.filter((g) => isSecondaryCategory(g.categoryName)),
    [allGroups]
  )

  const [showSecondary, setShowSecondary] = useState(false)
  const inLibrary = useInLibrary()

  if (allGroups.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border-primary bg-bg-secondary/40 p-10 text-center text-sm text-text-tertiary">
        No nominations recorded for this ceremony.
      </div>
    )
  }

  const renderGroup = (group: CategoryGroup) => {
    const winners = group.nominations.filter((n) => n.isWinner)
    const nominees = group.nominations.filter((n) => !n.isWinner)

    return (
      <section
        // Scoping the section key with the edition id forces React to
        // unmount every category when the user picks a different year.
        // Without this the category id collides across editions (e.g.
        // "best-picture" exists in every Oscars year), so React would
        // reconcile `<NomineeCard>` instances in place and reuse the
        // stale per-card state from `useTmdbLazyMovie` — that hook keeps
        // a cached `details` and its effect short-circuits when
        // `details` is already truthy, so the old year's poster/title
        // would linger on top of the newly-fetched data.
        key={`${edition.id}:${group.categoryId}`}
        aria-labelledby={`cat-${group.categoryId}`}
      >
        <h3
          id={`cat-${group.categoryId}`}
          className="mb-4 flex items-center gap-3 border-b border-white/5 pb-2 text-lg font-bold text-text-primary sm:text-xl"
        >
          <span
            className="h-5 w-1 rounded-full bg-accent-primary"
            aria-hidden
          />
          {group.categoryName}
        </h3>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-5">
          {winners.map((nom, idx) => {
            const inLib =
              nom.movieTmdbId != null && inLibrary.movies.has(nom.movieTmdbId)
            // Keys include edition id + tmdb id (or fallback index) so
            // swapping years forces a fresh NomineeCard mount instead
            // of reusing the previous edition's internal state.
            const cardKey = `${edition.id}:${group.categoryId}:w:${nom.movieTmdbId ?? `i${idx}`}`
            return (
              <div key={cardKey} className="col-span-2 row-span-1">
                <NomineeCard
                  nom={nom}
                  isWinner
                  inLibrary={inLib}
                  onOpen={() =>
                    nom.movieTmdbId != null &&
                    onOpenDetail(nom.movieTmdbId, 'movie')
                  }
                />
              </div>
            )
          })}

          {nominees.map((nom, idx) => {
            const inLib =
              nom.movieTmdbId != null && inLibrary.movies.has(nom.movieTmdbId)
            const cardKey = `${edition.id}:${group.categoryId}:n:${nom.movieTmdbId ?? `i${idx}`}`
            return (
              <NomineeCard
                key={cardKey}
                nom={nom}
                isWinner={false}
                inLibrary={inLib}
                onOpen={() =>
                  nom.movieTmdbId != null &&
                  onOpenDetail(nom.movieTmdbId, 'movie')
                }
              />
            )
          })}
        </div>
      </section>
    )
  }

  return (
    <div className="space-y-10">
      {main.length === 0 && secondary.length > 0 && (
        <div className="text-xs text-text-tertiary">
          Only technical / craft awards are recorded for this ceremony.
        </div>
      )}

      {main.map(renderGroup)}

      {secondary.length > 0 && (
        <div className="border-t border-white/10 pt-8">
          <button
            type="button"
            onClick={() => setShowSecondary((s) => !s)}
            className="flex w-full items-center justify-center gap-3 rounded-xl bg-white/5 px-6 py-4 text-sm font-semibold text-text-secondary transition-colors hover:bg-white/10 hover:text-text-primary"
            aria-expanded={showSecondary}
          >
            <span>
              {showSecondary ? 'Hide' : 'Show'} technical &amp; other awards
            </span>
            <span className="text-xs text-text-tertiary">
              ({secondary.length} categories)
            </span>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform motion-reduce:transition-none ${
                showSecondary ? 'rotate-180' : ''
              }`}
              aria-hidden
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {showSecondary && (
            <div className="mt-10 space-y-10">{secondary.map(renderGroup)}</div>
          )}
        </div>
      )}
    </div>
  )
}
